import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";
import { CreateContactsPicker } from "../ContactsPicker";
import { CreateOrganizationsPicker } from "../OrganizationsPicker";
import { CreateClaimsPicker } from "../ClaimsPicker";
import { apiClient } from "@/lib/api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { CreatePropertyRequest, Property } from "@/api/generated/client/models";

const EMPTY_FORM: CreatePropertyRequest = { streetAddress: "", city: "", state: "", zipCode: "" };

interface PropertyCreateDialogProps {
    open: boolean;
    onClose: () => void;
    createProperty: (data: CreatePropertyRequest) => Promise<Property>;
    isCreating: boolean;
    /** When set, newly created property is auto-linked to these claims. */
    initialClaimIds?: number[];
    /** Hide the in-dialog claims picker (used when opened from a claim context). */
    hideClaimsPicker?: boolean;
    /** Called after a successful create (and link attempts). */
    onCreated?: () => void;
}

export function PropertyCreateDialog({ open, onClose, createProperty, initialClaimIds, hideClaimsPicker, onCreated }: PropertyCreateDialogProps) {
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const [form, setForm] = useState<CreatePropertyRequest>({ ...EMPTY_FORM });
    const [pendingContactIds, setPendingContactIds] = useState<number[]>([]);
    const [pendingOrgIds, setPendingOrgIds] = useState<number[]>([]);
    const [pendingClaimIds, setPendingClaimIds] = useState<number[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Seed claim links when opened from a claim context.
    useEffect(() => {
        if (open && initialClaimIds && initialClaimIds.length > 0) {
            setPendingClaimIds(initialClaimIds);
        }
    }, [open, initialClaimIds]);

    const handleClose = () => {
        onClose();
        setPendingContactIds([]);
        setPendingOrgIds([]);
        setPendingClaimIds([]);
        setForm({ ...EMPTY_FORM });
    };

    const handleCreate = async () => {
        setIsSubmitting(true);
        try {
            const created = await createProperty(form);
            const propId = created?.id;
            const hasLinks = pendingContactIds.length > 0 || pendingOrgIds.length > 0 || pendingClaimIds.length > 0;
            const linkFailures: string[] = [];
            if (propId && pendingContactIds.length > 0) {
                for (const contactId of pendingContactIds) {
                    try {
                        await apiClient.properties.linkPropertyContact({
                            id: propId,
                            linkPropertyContactRequest: { contactId },
                        });
                    } catch { linkFailures.push(`Contact #${contactId}`); }
                }
            }
            if (propId && pendingOrgIds.length > 0) {
                for (const organizationId of pendingOrgIds) {
                    try {
                        await apiClient.properties.linkPropertyOrganization({
                            id: propId,
                            linkPropertyOrganizationRequest: { organizationId },
                        });
                    } catch { linkFailures.push(`Organization #${organizationId}`); }
                }
            }
            if (propId && pendingClaimIds.length > 0) {
                for (const claimId of pendingClaimIds) {
                    try {
                        await apiClient.claims.linkClaimProperty({
                            claimId,
                            linkClaimPropertyRequest: { propertyId: propId },
                        });
                    } catch { linkFailures.push(`Claim #${claimId}`); }
                }
            }
            if (propId && hasLinks) {
                queryClient.invalidateQueries({ queryKey: ["property-contacts", propId] });
                queryClient.invalidateQueries({ queryKey: ["property-organizations", propId] });
                queryClient.invalidateQueries({ queryKey: ["property-claims", propId] });
                queryClient.invalidateQueries({ queryKey: ["contact-properties"] });
                queryClient.invalidateQueries({ queryKey: ["organization-properties"] });
            }
            if (linkFailures.length > 0) {
                toast({
                    title: "Property created, but some links failed",
                    description: `Failed to link: ${linkFailures.join(", ")}. You can retry from the property detail page.`,
                    variant: "destructive",
                });
            }
            onCreated?.();
            handleClose();
        } catch {
            // Error toast handled by mutation's onError callback
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
            <DialogContent className="sm:max-w-lg max-h-[85dvh] sm:max-h-[85vh] overflow-y-auto sm:overflow-auto">
                <DialogHeader>
                    <DialogTitle>Add Property</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <div className="space-y-2">
                        <Label>Street Address <span className="text-destructive">*</span></Label>
                        <Input value={form.streetAddress} onChange={(e) => setForm({ ...form, streetAddress: e.target.value })} placeholder="123 Main St" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>Unit / Suite</Label>
                            <Input value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value || null })} placeholder="Apt 4B" />
                        </div>
                        <div className="space-y-2">
                            <Label>City <span className="text-destructive">*</span></Label>
                            <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label>State <span className="text-destructive">*</span></Label>
                            <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="TX" />
                        </div>
                        <div className="space-y-2">
                            <Label>ZIP Code <span className="text-destructive">*</span></Label>
                            <Input value={form.zipCode} onChange={(e) => setForm({ ...form, zipCode: e.target.value })} placeholder="75001" />
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label>Notes</Label>
                        <Textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value || null })} placeholder="Additional notes..." rows={3} />
                    </div>
                    <Separator />
                    <CreateContactsPicker selectedIds={pendingContactIds} onChange={setPendingContactIds} />
                    <CreateOrganizationsPicker selectedIds={pendingOrgIds} onChange={setPendingOrgIds} />
                    {!hideClaimsPicker && (
                        <CreateClaimsPicker selectedIds={pendingClaimIds} onChange={setPendingClaimIds} />
                    )}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={!form.streetAddress || !form.city || !form.state || !form.zipCode || isSubmitting}>
                        {isSubmitting ? "Creating..." : "Create Property"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
